import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode, type Ref, useEffect, useId, useRef, useState } from 'react'
import { Spinner } from './Spinner.js'

/**
 * Een knop die op de pod wacht (besluit van 30 oktober 2026; ontwerp 6.4, "Een knop die wacht").
 *
 * Goedkeuren, Indienen, Oordeel vastleggen, Versturen en de andere handelingen die naar de pod
 * gaan, lieten na een klik een ogenblik niets zien, en wie niets ziet klikt nog een keer. Deze knop
 * is het ene patroon daarvoor:
 *
 * - **Direct bezig**, in dezelfde render als de klik: het wachtteken ({@link Spinner}) en een werkwoord
 *   ("Bezig met goedkeuren…"). De knop houdt zijn maat, want rust- en bezigtekst staan in dezelfde
 *   cel van een raster en de knop is zo breed als de langste; er verspringt niets.
 * - **`aria-busy` en `aria-disabled`, geen `disabled`.** Een uitgeschakelde knop verliest de focus,
 *   en dan staat een toetsenbordgebruiker na zijn klik nergens. Een tweede klik of Enter doet niets
 *   zolang hij bezig is; ook een formulier dat hij verstuurt wordt niet nog een keer verstuurd.
 * - **Minstens 300 milliseconden bezig**, zodat een snel antwoord geen flits geeft (dezelfde
 *   gedachte als valkuil 7 bij de opslagindicator).
 * - **Mislukt het, dan staat hij weer zoals hij was**, met een melding die aan de knop hangt
 *   (`aria-describedby`, R-140) en als `role="alert"` wordt voorgelezen. Lukt het, dan verandert
 *   het scherm zelf: de pagina toont de nieuwe stand, en daar gaat de focus heen.
 *
 * Twee manieren van gebruik. Met `onAction`: geeft die een belofte terug, dan is de knop bezig tot
 * ze is afgehandeld; geeft hij niets terug (bijvoorbeeld als er alleen een overzicht opengaat), dan
 * wordt hij niet bezig. Of gestuurd met `busy`, voor een formulier dat zichzelf verstuurt.
 */
export interface BusyButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'> {
  readonly children: ReactNode
  /** Wat de knop zegt terwijl hij wacht, met een werkwoord: "Bezig met goedkeuren…". */
  readonly busyText: string
  readonly onAction?: () => unknown
  /** Bezig van buiten, voor een knop die een formulier verstuurt. */
  readonly busy?: boolean
  /** De melding als de handeling mislukt; zonder deze tekst een algemene. */
  readonly errorText?: string
  readonly buttonRef?: Ref<HTMLButtonElement>
}

/** Hoe lang de bezigstand minstens staat, in milliseconden. */
export const MINIMUM_BUSY_MS = 300

export function BusyButton({
  children,
  busyText,
  onAction,
  busy: busyFromOutside = false,
  errorText,
  buttonRef,
  className,
  type = 'button',
  'aria-describedby': describedBy,
  ...rest
}: BusyButtonProps): ReactNode {
  const [ownBusy, setOwnBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  // Synchroon naast de state: twee klikken in hetzelfde frame zien de state nog niet veranderd.
  const working = useRef(false)
  const mounted = useRef(true)
  const errorId = useId()
  const shownOutside = useMinimumVisible(busyFromOutside)
  const busy = ownBusy || shownOutside

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  async function run(): Promise<void> {
    const outcome = onAction?.()
    if (!(outcome instanceof Promise)) return
    working.current = true
    setOwnBusy(true)
    setFailed(false)
    const start = Date.now()
    let succeeded = true
    try {
      await outcome
    } catch {
      succeeded = false
    }
    const remaining = MINIMUM_BUSY_MS - (Date.now() - start)
    if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining))
    working.current = false
    if (!mounted.current) return
    setOwnBusy(false)
    if (!succeeded) setFailed(true)
  }

  const description = [describedBy, failed ? errorId : undefined].filter((part) => part !== undefined).join(' ')

  return (
    <>
      <button
        {...rest}
        ref={buttonRef}
        type={type}
        className={['ef-bezigknop', className].filter((part) => part !== undefined).join(' ')}
        data-bezig={busy ? 'ja' : undefined}
        aria-busy={busy ? 'true' : undefined}
        aria-disabled={busy ? 'true' : rest['aria-disabled']}
        // Bezig heet de knop naar wat hij doet; de tekst staat als gegenereerde inhoud, en die
        // tellen niet alle hulpmiddelen mee.
        {...(busy ? { 'aria-label': busyText } : {})}
        {...(description === '' ? {} : { 'aria-describedby': description })}
        onClick={(event) => {
          if (busy || working.current || busyFromOutside) {
            event.preventDefault()
            return
          }
          void run()
        }}
      >
        <span className="ef-bezigknop__rust">{children}</span>
        {/*
          De bezigtekst als gegenereerde inhoud (`::after` met `data-tekst`) en niet als tekst in de
          DOM: zo blijft de tekst van de knop wat er in rust staat, voor wie hem zoekt of leest, en
          noemt de browser hem pas bij die tekst als hij zichtbaar is.
        */}
        <span className="ef-bezigknop__bezig" data-tekst={busyText}>
          <Spinner />
        </span>
      </button>
      {failed ? (
        <span id={errorId} role="alert" className="ef-bezigknop__fout">
          {errorText ?? 'Dat is niet gelukt. Probeer het opnieuw.'}
        </span>
      ) : null}
    </>
  )
}

/** Een bezigstand die, eenmaal aan, minstens {@link MINIMUM_BUSY_MS} blijft staan. */
function useMinimumVisible(busy: boolean): boolean {
  const [shown, setShown] = useState(busy)
  const since = useRef<number | undefined>(busy ? Date.now() : undefined)
  useEffect(() => {
    if (busy) {
      since.current = Date.now()
      setShown(true)
      return
    }
    if (since.current === undefined) return
    const remaining = MINIMUM_BUSY_MS - (Date.now() - since.current)
    if (remaining <= 0) {
      setShown(false)
      return
    }
    const timer = setTimeout(() => setShown(false), remaining)
    return () => clearTimeout(timer)
  }, [busy])
  return busy || shown
}

/**
 * Een link die naar een andere pagina gaat en daar even op wacht, zoals "Opnieuw inloggen": na de
 * klik dezelfde bezigstand als {@link BusyButton}, tot de browser de nieuwe pagina laat zien. Een
 * klik met een modifier (nieuw tabblad) laat de pagina staan en wordt dus niet bezig. Een tweede
 * klik doet niets.
 */
export interface BusyLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
  readonly children: ReactNode
  readonly busyText: string
}

export function BusyLink({ children, busyText, className, onClick, ...rest }: BusyLinkProps): ReactNode {
  const [busy, setBusy] = useState(false)
  return (
    <a
      {...rest}
      className={['ef-bezigknop', className].filter((part) => part !== undefined).join(' ')}
      data-bezig={busy ? 'ja' : undefined}
      aria-busy={busy ? 'true' : undefined}
      aria-disabled={busy ? 'true' : undefined}
      {...(busy ? { 'aria-label': busyText } : {})}
      onClick={(event) => {
        if (busy) {
          event.preventDefault()
          return
        }
        onClick?.(event)
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
        setBusy(true)
      }}
    >
      <span className="ef-bezigknop__rust">{children}</span>
      <span className="ef-bezigknop__bezig" data-tekst={busyText}>
        <Spinner />
      </span>
    </a>
  )
}
